import io from 'socket.io-client';
import { listChannels } from '../../shared/services/channelService';
import { openAgentPhoneWindow, isAgentPhoneWindow } from '../agentPhone/agentPhoneBridge';
import {
  PTT_MAX_DURATION_MS,
  PTT_SAMPLE_RATE,
  PttPlaybackQueue,
  startPttCapture,
} from '../../features/team-chat/utils/pttAudio';
import {
  getPttTabId,
  isPttAudioLeader,
  publishPttBridgeMessage,
  publishPttPhoneReady,
  refreshPttAudioLeader,
  releasePttAudioLeader,
  subscribePttBridgeMessages,
  tryClaimPttAudioLeader,
} from './pttBridge';
import type { GlobalPttState, PttBridgeMessage, PttSpeaker } from './types';

type AppSocket = ReturnType<typeof io>;

type StateListener = (state: GlobalPttState) => void;

const initialState: GlobalPttState = {
  remoteSpeaker: null,
  isReceiving: false,
  isPlayingLocally: false,
  isTransmitting: false,
  transmitChannelId: null,
  micError: null,
};

class GlobalPttService {
  private socket: AppSocket | null = null;
  private userId = '';
  private joinedChannelIds = new Set<string>();
  private playbacks = new Map<string, PttPlaybackQueue>();
  private activeRemoteSpeakers = new Set<string>();
  private lastChunkSeqBySpeaker = new Map<string, number>();
  private state: GlobalPttState = { ...initialState };
  private listeners = new Set<StateListener>();
  private bridgeUnsub: (() => void) | null = null;
  private leaderHeartbeat: number | null = null;
  private phoneSinkActive = false;
  private phoneReady = false;
  private captureRef: { stop: () => void } | null = null;
  private transmitSeq = 0;
  private transmitMaxTimer: number | null = null;
  private transmitting = false;

  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  getState(): GlobalPttState {
    return this.state;
  }

  getRemoteSpeakerForChannel(channelId: string | null): PttSpeaker | null {
    if (!channelId || !this.state.remoteSpeaker) return null;
    return this.state.remoteSpeaker.channel_id === channelId ? this.state.remoteSpeaker : null;
  }

  /** Recipient stops playback and signals the speaker to end their transmission. */
  cutIncoming(cutterDisplayName?: string): void {
    const speaker = this.state.remoteSpeaker;
    if (!speaker || !this.socket || !this.userId) return;

    this.stopSpeakerLocally(speaker.user_id);

    this.socket.emit('channel:ptt:cut', {
      channel_id: speaker.channel_id,
      user_id: this.userId,
      target_user_id: speaker.user_id,
      display_name: cutterDisplayName?.trim() || 'Recipient',
    });
  }

  /** Hold-to-talk on a DM channel (initiate or reply). Single global mic — safe across tabs. */
  async startTransmit(channelId: string, displayName: string): Promise<void> {
    if (!this.socket?.connected || !this.userId || !channelId || this.transmitting) return;

    this.patchState({ micError: null });
    this.transmitSeq = 0;

    this.socket.emit('channel:ptt:start', {
      channel_id: channelId,
      user_id: this.userId,
      display_name: displayName,
      sample_rate: PTT_SAMPLE_RATE,
    });

    try {
      const capture = await startPttCapture((chunk) => {
        if (!this.transmitting || !this.socket) return;
        this.socket.emit('channel:ptt:chunk', {
          channel_id: channelId,
          user_id: this.userId,
          chunk,
          seq: this.transmitSeq++,
        });
      });
      this.captureRef = capture;
      this.transmitting = true;
      this.patchState({ isTransmitting: true, transmitChannelId: channelId });

      this.transmitMaxTimer = window.setTimeout(() => {
        this.stopTransmit();
      }, PTT_MAX_DURATION_MS);
    } catch (err) {
      this.transmitting = false;
      this.patchState({
        isTransmitting: false,
        transmitChannelId: null,
        micError: err instanceof Error ? err.message : 'Microphone access denied',
      });
      this.socket.emit('channel:ptt:end', { channel_id: channelId, user_id: this.userId });
    }
  }

  stopTransmit(): void {
    if (this.transmitMaxTimer != null) {
      window.clearTimeout(this.transmitMaxTimer);
      this.transmitMaxTimer = null;
    }
    const channelId = this.state.transmitChannelId;
    this.captureRef?.stop();
    this.captureRef = null;
    this.transmitting = false;
    this.patchState({ isTransmitting: false, transmitChannelId: null });

    if (this.socket && channelId && this.userId) {
      this.socket.emit('channel:ptt:end', { channel_id: channelId, user_id: this.userId });
    }
  }

  private stopSpeakerLocally(speakerId: string): void {
    const playback = this.playbacks.get(speakerId);
    playback?.reset();
    this.playbacks.delete(speakerId);
    this.activeRemoteSpeakers.delete(speakerId);
    this.lastChunkSeqBySpeaker.delete(speakerId);

    const stillReceiving = this.activeRemoteSpeakers.size > 0;
    if (this.state.remoteSpeaker?.user_id === speakerId) {
      if (!stillReceiving) {
        this.patchState({ remoteSpeaker: null, isReceiving: false, isPlayingLocally: false });
      } else {
        this.patchState({ remoteSpeaker: null });
      }
    }
  }

  async init(socket: AppSocket | null, userId: string): Promise<void> {
    if (!socket || !userId) {
      this.destroy();
      return;
    }

    if (this.socket === socket && this.userId === userId) {
      await this.refreshDmChannelRooms();
      if (isAgentPhoneWindow()) publishPttPhoneReady();
      return;
    }

    this.destroy();
    this.socket = socket;
    this.userId = userId;
    this.attachSocketHandlers();
    this.bridgeUnsub = subscribePttBridgeMessages((msg) => this.handleBridgeMessage(msg));
    await this.refreshDmChannelRooms();
    if (isAgentPhoneWindow()) publishPttPhoneReady();
  }

  destroy(): void {
    this.stopTransmit();
    this.detachSocketHandlers();
    this.bridgeUnsub?.();
    this.bridgeUnsub = null;
    this.leaveAllChannelRooms();
    for (const playback of this.playbacks.values()) {
      playback.close();
    }
    this.playbacks.clear();
    this.activeRemoteSpeakers.clear();
    this.lastChunkSeqBySpeaker.clear();
    releasePttAudioLeader();
    if (this.leaderHeartbeat != null) {
      window.clearInterval(this.leaderHeartbeat);
      this.leaderHeartbeat = null;
    }
    this.socket = null;
    this.userId = '';
    this.phoneSinkActive = false;
    this.phoneReady = false;
    this.patchState({ ...initialState });
  }

  private patchState(patch: Partial<GlobalPttState>): void {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((fn) => fn(this.state));
  }

  private canPlayAudioHere(): boolean {
    if (isAgentPhoneWindow()) return true;
    if (this.phoneSinkActive) return false;
    return isPttAudioLeader();
  }

  private shouldRelayToPhone(): boolean {
    return !isAgentPhoneWindow() && isPttAudioLeader() && this.phoneSinkActive && !this.phoneReady;
  }

  private getPlaybackForSpeaker(speakerId: string): PttPlaybackQueue {
    let queue = this.playbacks.get(speakerId);
    if (!queue) {
      queue = new PttPlaybackQueue();
      this.playbacks.set(speakerId, queue);
    }
    return queue;
  }

  private shouldPlayChunk(speakerId: string, seq: number | undefined): boolean {
    const chunkSeq = seq ?? 0;
    const last = this.lastChunkSeqBySpeaker.get(speakerId);
    if (last != null && chunkSeq <= last) {
      return false;
    }
    this.lastChunkSeqBySpeaker.set(speakerId, chunkSeq);
    return true;
  }

  private handleIncomingStart(data: {
    channel_id: string;
    user_id: string;
    display_name?: string;
    sample_rate?: number;
  }): void {
    if (!data.channel_id || data.user_id === this.userId) return;

    const speaker: PttSpeaker = {
      channel_id: data.channel_id,
      user_id: data.user_id,
      display_name: data.display_name?.trim() || 'Someone',
    };

    this.activeRemoteSpeakers.add(data.user_id);
    this.lastChunkSeqBySpeaker.delete(data.user_id);
    this.patchState({ remoteSpeaker: speaker, isReceiving: true });

    const playback = this.getPlaybackForSpeaker(data.user_id);
    void playback.ensureContext(data.sample_rate ?? PTT_SAMPLE_RATE).then(() => {
      playback.reset();
    });

    if (isAgentPhoneWindow()) {
      this.patchState({ isPlayingLocally: true });
      publishPttPhoneReady();
      return;
    }

    const phoneWin = openAgentPhoneWindow();
    if (phoneWin) {
      tryClaimPttAudioLeader();
      this.phoneSinkActive = true;
      this.patchState({ isPlayingLocally: false });

      if (isPttAudioLeader()) {
        if (this.leaderHeartbeat == null) {
          this.leaderHeartbeat = window.setInterval(() => refreshPttAudioLeader(), 2000);
        }
        publishPttBridgeMessage({
          type: 'PTT_START',
          channel_id: data.channel_id,
          user_id: data.user_id,
          display_name: speaker.display_name,
          sample_rate: data.sample_rate ?? PTT_SAMPLE_RATE,
        });
      }
      return;
    }

    const claimed = tryClaimPttAudioLeader();
    if (claimed) {
      if (this.leaderHeartbeat == null) {
        this.leaderHeartbeat = window.setInterval(() => refreshPttAudioLeader(), 2000);
      }
      this.phoneSinkActive = false;
      this.patchState({ isPlayingLocally: true });
    }
  }

  private handleIncomingChunk(data: {
    channel_id: string;
    user_id: string;
    chunk: string;
    seq?: number;
  }): void {
    if (!data.channel_id || data.user_id === this.userId || !data.chunk) return;
    if (!this.shouldPlayChunk(data.user_id, data.seq)) return;

    if (this.shouldRelayToPhone()) {
      publishPttBridgeMessage({
        type: 'PTT_CHUNK',
        channel_id: data.channel_id,
        user_id: data.user_id,
        chunk: data.chunk,
        seq: data.seq ?? 0,
      });
      return;
    }

    if (!this.canPlayAudioHere()) return;
    void this.getPlaybackForSpeaker(data.user_id).playChunk(data.chunk);
  }

  private handleIncomingEnd(data: { channel_id: string; user_id: string }): void {
    if (!data.channel_id || data.user_id === this.userId) return;

    if (this.shouldRelayToPhone()) {
      publishPttBridgeMessage({
        type: 'PTT_END',
        channel_id: data.channel_id,
        user_id: data.user_id,
      });
    }

    const playback = this.playbacks.get(data.user_id);
    playback?.reset();
    this.stopSpeakerLocally(data.user_id);
  }

  private handleBridgeMessage(msg: PttBridgeMessage): void {
    if (!msg || msg.source_tab_id === getPttTabId()) return;

    if (msg.type === 'PTT_PHONE_READY') {
      if (!isAgentPhoneWindow()) {
        this.phoneReady = true;
        releasePttAudioLeader();
        if (this.leaderHeartbeat != null) {
          window.clearInterval(this.leaderHeartbeat);
          this.leaderHeartbeat = null;
        }
      }
      return;
    }

    if (!isAgentPhoneWindow()) return;

    // Agent phone has its own socket; bridge chunks duplicate socket playback.
    if (msg.type === 'PTT_CHUNK') {
      return;
    }

    if (msg.type === 'PTT_START') {
      if (this.activeRemoteSpeakers.has(msg.user_id)) {
        return;
      }
      const speaker: PttSpeaker = {
        channel_id: msg.channel_id,
        user_id: msg.user_id,
        display_name: msg.display_name,
      };
      this.activeRemoteSpeakers.add(msg.user_id);
      this.patchState({ remoteSpeaker: speaker, isReceiving: true, isPlayingLocally: true });
      void this.getPlaybackForSpeaker(msg.user_id)
        .ensureContext(msg.sample_rate)
        .then(() => this.getPlaybackForSpeaker(msg.user_id).reset());
      publishPttPhoneReady();
      return;
    }

    if (msg.type === 'PTT_END') {
      const playback = this.playbacks.get(msg.user_id);
      playback?.reset();
      this.playbacks.delete(msg.user_id);
      this.activeRemoteSpeakers.delete(msg.user_id);
      if (this.activeRemoteSpeakers.size === 0) {
        this.patchState({ remoteSpeaker: null, isReceiving: false, isPlayingLocally: false });
      }
    }
  }

  private attachSocketHandlers(): void {
    const socket = this.socket;
    if (!socket) return;
    socket.on('connect', this.onSocketConnect);
    socket.on('channel:created', this.onChannelCreated);
    socket.on('channel:ptt:start', this.onPttStart);
    socket.on('channel:ptt:chunk', this.onPttChunk);
    socket.on('channel:ptt:end', this.onPttEnd);
    socket.on('channel:ptt:cut', this.onPttCut);
  }

  private detachSocketHandlers(): void {
    const socket = this.socket;
    if (!socket) return;
    socket.off('connect', this.onSocketConnect);
    socket.off('channel:created', this.onChannelCreated);
    socket.off('channel:ptt:start', this.onPttStart);
    socket.off('channel:ptt:chunk', this.onPttChunk);
    socket.off('channel:ptt:end', this.onPttEnd);
    socket.off('channel:ptt:cut', this.onPttCut);
  }

  private onSocketConnect = (): void => {
    void this.refreshDmChannelRooms();
    if (isAgentPhoneWindow()) publishPttPhoneReady();
  };

  private onChannelCreated = (data: {
    channel?: { channel_id?: string; type?: string; scope?: string };
  }): void => {
    const ch = data?.channel;
    if (ch?.type === 'dm' && ch?.scope === 'admin' && ch.channel_id) {
      this.joinChannelRoom(ch.channel_id);
    }
  };

  private onPttStart = (data: {
    channel_id: string;
    user_id: string;
    display_name?: string;
    sample_rate?: number;
  }): void => {
    this.handleIncomingStart(data);
  };

  private onPttChunk = (data: {
    channel_id: string;
    user_id: string;
    chunk: string;
    seq?: number;
  }): void => {
    this.handleIncomingChunk(data);
  };

  private onPttEnd = (data: { channel_id: string; user_id: string }): void => {
    this.handleIncomingEnd(data);
  };

  private onPttCut = (data: {
    channel_id: string;
    user_id: string;
    cut_by_name?: string;
  }): void => {
    if (data.user_id !== this.userId || !this.transmitting) return;
    this.stopTransmit();
    this.patchState({ micError: 'Stopped by the other person' });
    window.setTimeout(() => {
      if (this.state.micError === 'Stopped by the other person') {
        this.patchState({ micError: null });
      }
    }, 3000);
  };

  private joinChannelRoom(channelId: string): void {
    if (!this.socket || !channelId || this.joinedChannelIds.has(channelId)) return;
    this.socket.emit('channel:join', { channel_id: channelId });
    this.joinedChannelIds.add(channelId);
  }

  private leaveChannelRoom(channelId: string): void {
    if (!this.socket || !this.joinedChannelIds.has(channelId)) return;
    this.socket.emit('channel:leave', { channel_id: channelId });
    this.joinedChannelIds.delete(channelId);
  }

  private leaveAllChannelRooms(): void {
    for (const id of this.joinedChannelIds) {
      this.leaveChannelRoom(id);
    }
    this.joinedChannelIds.clear();
  }

  private async refreshDmChannelRooms(): Promise<void> {
    if (!this.socket || !this.userId) return;
    try {
      const result = await listChannels('admin');
      const dmIds = new Set(
        result.channels.filter((c) => c.type === 'dm').map((c) => c.channel_id)
      );
      for (const id of dmIds) this.joinChannelRoom(id);
      for (const id of [...this.joinedChannelIds]) {
        if (!dmIds.has(id)) this.leaveChannelRoom(id);
      }
    } catch (err) {
      console.error('[GlobalPtt] Failed to refresh DM channel rooms:', err);
    }
  }
}

export const globalPttService = new GlobalPttService();
